import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  FaBars,
  FaBoxOpen,
  FaChartPie,
  FaClipboardList,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaHeart,
  FaHome,
  FaLayerGroup,
  FaPlus,
  FaQuoteLeft,
  FaShoppingCart,
  FaSignOutAlt,
  FaStore,
  FaTimes,
  FaTruck,
  FaUsers,
  FaPaintBrush,
  FaBookOpen,
  FaStar,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { FormPageSkeleton } from "../components/Skeleton";
import "./DashboardLayout.css";

const ROLE_RANK = { customer: 1, sales: 2, admin: 3 };
const SIDEBAR_COLLAPSED_KEY = "sgoffice_dash_sidebar_collapsed";

function hasRole(userRole, minRole) {
  return (ROLE_RANK[userRole] || 0) >= (ROLE_RANK[minRole] || 0);
}

const NAV_SECTIONS = [
  {
    title: "Overview",
    minRole: "customer",
    items: [{ to: "/dashboard", label: "Dashboard", icon: FaChartPie, end: true }],
  },
  {
    title: "My account",
    minRole: "customer",
    items: [
      { to: "/dashboard/orders", label: "My Orders", icon: FaClipboardList },
      { to: "/dashboard/track", label: "Track Order", icon: FaTruck },
      { to: "/dashboard/wishlist", label: "Wishlist", icon: FaHeart },
      { to: "/dashboard/cart", label: "Cart", icon: FaShoppingCart },
    ],
  },
  {
    title: "Sales",
    minRole: "sales",
    items: [
      { to: "/dashboard/manage/orders", label: "All Orders", icon: FaClipboardList },
      { to: "/dashboard/manage/quotes", label: "Quote Requests", icon: FaQuoteLeft },
      {
        to: "/dashboard/manage/enquiries",
        label: "Enquiries",
        icon: FaEnvelopeOpenText,
      },
    ],
  },
  {
    title: "Catalog",
    minRole: "admin",
    items: [
      { to: "/dashboard/products", label: "Products", icon: FaBoxOpen, end: true },
      { to: "/dashboard/products/new", label: "Add Product", icon: FaPlus },
      { to: "/dashboard/categories", label: "Categories", icon: FaLayerGroup },
      { to: "/dashboard/reviews", label: "Reviews", icon: FaStar },
    ],
  },
  {
    title: "Website",
    minRole: "admin",
    items: [
      { to: "/dashboard/home", label: "Home Page", icon: FaHome },
      { to: "/dashboard/sign-graphics", label: "Sign & Graphics", icon: FaPaintBrush },
      { to: "/dashboard/catalogue", label: "Catalogue", icon: FaBookOpen },
      { to: "/dashboard/settings/email", label: "Email Settings", icon: FaEnvelope },
    ],
  },
  {
    title: "People",
    minRole: "admin",
    items: [{ to: "/dashboard/users", label: "Customers & Staff", icon: FaUsers }],
  },
];

const DashboardLayout = () => {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [flyout, setFlyout] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  useEffect(() => {
    setFlyout(null);
  }, [collapsed, location.pathname]);

  if (loading) {
    return (
      <div className="dash-shell">
        <div className="dash-main" style={{ padding: 24 }}>
          <FormPageSkeleton />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate to="/login" state={{ from: location.pathname }} replace />
    );
  }

  const role = user?.role || "customer";
  const sections = NAV_SECTIONS.filter((s) => hasRole(role, s.minRole));

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
  };

  const closeMobile = () => setMobileOpen(false);

  const showFlyout = (label, event) => {
    if (!collapsed) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setFlyout({
      label,
      top: rect.top,
      left: rect.right,
      height: rect.height,
    });
  };

  const hideFlyout = () => setFlyout(null);

  return (
    <div className={`dash-shell ${collapsed ? "is-collapsed" : ""}`}>
      <aside
        className={`dash-sidebar ${mobileOpen ? "open" : ""} ${
          collapsed ? "collapsed" : ""
        }`}
      >
        <div className="dash-brand">
          <Link to="/dashboard" onClick={closeMobile}>
            <span className="dash-brand-mark">SG</span>
            <span className="dash-brand-text">
              SG OFFICE
              <span>Control Panel</span>
            </span>
          </Link>
          <button
            type="button"
            className="dash-close"
            aria-label="Close menu"
            onClick={closeMobile}
          >
            <FaTimes />
          </button>
          <button
            type="button"
            className="dash-collapse-btn"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        <nav className="dash-nav" onScroll={hideFlyout}>
          {sections.map((section) => (
            <div className="dash-nav-section" key={section.title}>
              <p className="dash-nav-title">{section.title}</p>
              {section.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    `dash-nav-link ${isActive ? "active" : ""} ${
                      collapsed && flyout?.label === label ? "is-flyout-open" : ""
                    }`
                  }
                  onClick={closeMobile}
                  onMouseEnter={(e) => showFlyout(label, e)}
                  onMouseLeave={hideFlyout}
                  onFocus={(e) => showFlyout(label, e)}
                  onBlur={hideFlyout}
                >
                  <Icon />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="dash-sidebar-footer">
          <Link
            to="/"
            className={`dash-store-link ${
              collapsed && flyout?.label === "View store" ? "is-flyout-open" : ""
            }`}
            onMouseEnter={(e) => showFlyout("View store", e)}
            onMouseLeave={hideFlyout}
            onFocus={(e) => showFlyout("View store", e)}
            onBlur={hideFlyout}
          >
            <FaStore />
            <span>View store</span>
          </Link>
        </div>
      </aside>

      {collapsed && flyout && (
        <div
          className="dash-flyout-label"
          style={{
            top: flyout.top,
            left: flyout.left,
            height: flyout.height,
          }}
          aria-hidden="true"
        >
          {flyout.label}
        </div>
      )}

      {mobileOpen && (
        <div
          className="dash-backdrop"
          role="presentation"
          onClick={closeMobile}
        />
      )}

      <div className="dash-main">
        <header className="dash-topbar">
          <button
            type="button"
            className="dash-menu-btn"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <FaBars />
          </button>

          <button
            type="button"
            className="dash-topbar-collapse"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>

          <div className="dash-topbar-user">
            <div className="dash-user-meta">
              <strong>{user?.name || user?.email}</strong>
              <span className="dash-role">{role}</span>
            </div>
            <button
              type="button"
              className="dash-logout"
              onClick={handleLogout}
            >
              <FaSignOutAlt /> <span>Logout</span>
            </button>
          </div>
        </header>

        <div className="dash-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
